import { Item } from "@/api";
import SeparablePanel from "@/components/SeparablePanel";
import { formatDate } from "@/utils/format";
import { Panel } from "primereact/panel";
import { useMemo } from "react";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

interface FactoryChartProps {
  items: Item[];
  日付: string[];
  productions: Record<Item["品目ID"], number[]>;
  onDateClick?: (date: string) => void;
}

const FactoryChart: React.FC<FactoryChartProps> = ({
  items,
  日付,
  productions,
  onDateClick,
}) => {
  const dates = 日付.map(d=>formatDate(d));
  const tooltipFormatter = (value: string, name: string, props: unknown) => {
    return [parseFloat(value).toFixed(2), name.toString()];
  }
  
  // チャート要素のクリックハンドラ
  const handleChartClick = (data: unknown) => {
    if (data && typeof data === 'object' && 'activeLabel' in data && onDateClick) {
      const activeLabel = (data as { activeLabel: string }).activeLabel;
      // フォーマットされた日付から元の日付を見つける
      const clickedDateIndex = dates.findIndex(date => date === activeLabel);
      if (clickedDateIndex !== -1) {
        const originalDate = 日付[clickedDateIndex];
        onDateClick(originalDate);
      }
    }
  }
  
  const chartData = useMemo(()=>dates.map((date, index) => {
    const dataPoint: Record<string, string | number> = { date };
    let totalProceessTime = 0;
    let totalProduction = 0;
    Object.keys(productions).forEach(itemId => {
      // _oldがつくキーは除外して、現在の生産数のみを計算
      if (itemId.endsWith('_old')) return;

      const processTime = items.find(item => item.品目ID === itemId)?.工程時間;
      if(processTime) {
        totalProceessTime += processTime.value * productions[itemId][index];
      }
      totalProduction += productions[itemId][index];
    });
    dataPoint['totalProcessTime'] = totalProceessTime;
    dataPoint['totalProduction'] = totalProduction;
    return dataPoint;
  }), [items, productions, dates]);
  return (
    <SeparablePanel toggleable header="工場稼働率">
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart 
          data={chartData} 
          margin={{ top: 5, right: 30, left: 20, bottom: 70 }}
          onClick={handleChartClick}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            angle={-45}
            textAnchor="end"
            height={70}
            interval={0} // すべての日付を表示
            tick={{ fontSize: 10 }} // フォントサイズを小さくする
            tickFormatter={(date) => {
              const dayOfWeek = new Date(date).getDay();
              const dayName = ['日', '月', '火', '水', '木', '金', '土'][dayOfWeek];
              return `${date.replace(/^\d+-/,"")} (${dayName})`;
            }}
          />
          <YAxis
            yAxisId="left"
            domain={[0, 'auto']}
            label={{ value: "工程時間", angle: -90, position: 'insideLeft' }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 'auto']}
            label={{ value: "生産数", angle: 90, position: 'insideRight' }}
          />
          <Line type="monotone"
          yAxisId="left"
          name="稼働時間"
          dataKey="totalProcessTime"
          stroke="#8884d8" />
          <Bar
          yAxisId="right"
          name="生産数"
          dataKey="totalProduction"
          fill="#82ca9d"
          fillOpacity={0.6} />
          <Tooltip formatter={tooltipFormatter}/>
        </ComposedChart>
      </ResponsiveContainer>
    </SeparablePanel>
  )
}

export default FactoryChart;


